import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Devices as DevicesIcon,
  Block,
  CheckCircle,
  Refresh,
  Visibility,
  Security,
  PhoneAndroid,
  Headphones
} from '@mui/icons-material';
import adminApi from '../services/api';

interface Device {
  id: string;
  phone_mac: string;
  earbuds_mac: string;
  environment: string;
  is_blocked: boolean;
  first_seen: string;
  last_seen: string;
  total_requests: number;
  attestation_success_rate?: number;
  last_attestation_security_level?: string;
  strongbox_verified_count?: number;
}

const Devices: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [filterEnvironment, setFilterEnvironment] = useState<string>('all');
  const [filterBlocked, setFilterBlocked] = useState<string>('all');

  const loadDevices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {};
      if (filterEnvironment !== 'all') {
        params.environment = filterEnvironment;
      }
      if (filterBlocked === 'blocked') {
        params.blocked_only = true;
      }
      
      const response = await adminApi.getDevices(params);
      setDevices(response.devices || []);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load devices';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filterEnvironment, filterBlocked]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleDeviceAction = async (device: Device, action: 'block' | 'unblock') => {
    try {
      setActionLoading(device.id);
      await adminApi.deviceAction(device.id, {
        action,
        reason: actionReason || `${action} device via admin dashboard`
      });
      setOpenDialog(false);
      setSelectedDevice(null);
      setActionReason('');
      await loadDevices();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || `Failed to ${action} device`;
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const openActionDialog = (device: Device) => {
    setSelectedDevice(device);
    setActionReason('');
    setOpenDialog(true);
  };

  const getEnvironmentColor = (environment: string) => {
    switch (environment) {
      case 'production': return 'error';
      case 'staging': return 'warning';
      case 'testing': return 'info';
      default: return 'default';
    }
  };

  const getSecurityLevelColor = (level?: string) => {
    switch (level) {
      case 'STRONGBOX': return 'success';
      case 'TEE': return 'warning';
      case 'SOFTWARE': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatMacAddress = (mac: string) => {
    // If MAC already has colons in the right places, just return it uppercase
    if (mac.includes(':') && mac.length === 17) {
      return mac.toUpperCase();
    }
    // Otherwise, format it by adding colons every 2 characters
    return mac.toUpperCase().replace(/(.{2})/g, '$1:').slice(0, -1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  const activeDevices = devices.filter(d => !d.is_blocked);
  const blockedDevices = devices.filter(d => d.is_blocked);
  const strongboxDevices = devices.filter(d => d.last_attestation_security_level === 'STRONGBOX');

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          📱 Device Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadDevices}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Environment</InputLabel>
                <Select
                  value={filterEnvironment}
                  label="Environment"
                  onChange={(e) => setFilterEnvironment(e.target.value)}
                >
                  <MenuItem value="all">All Environments</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                  <MenuItem value="staging">Staging</MenuItem>
                  <MenuItem value="testing">Testing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterBlocked}
                  label="Status"
                  onChange={(e) => setFilterBlocked(e.target.value)}
                >
                  <MenuItem value="all">All Devices</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="blocked">Blocked Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Total Devices</Typography>
                  <Typography variant="h3" fontWeight="bold">{devices.length}</Typography>
                </Box>
                <DevicesIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Active Devices</Typography>
                  <Typography variant="h3" fontWeight="bold">{activeDevices.length}</Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Blocked Devices</Typography>
                  <Typography variant="h3" fontWeight="bold">{blockedDevices.length}</Typography>
                </Box>
                <Block sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>StrongBox Devices</Typography>
                  <Typography variant="h3" fontWeight="bold">{strongboxDevices.length}</Typography>
                </Box>
                <Security sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Devices Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Device List ({devices.length} devices)
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Device ID</TableCell>
                  <TableCell>Phone MAC</TableCell>
                  <TableCell>Earbuds MAC</TableCell>
                  <TableCell>Environment</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Security Level</TableCell>
                  <TableCell>Success Rate</TableCell>
                  <TableCell>Requests</TableCell>
                  <TableCell>First Seen</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No devices found. Devices will appear here when they make their first authentication request.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  devices.map((device) => (
                    <TableRow key={device.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {device.id.substring(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneAndroid fontSize="small" />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {formatMacAddress(device.phone_mac)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Headphones fontSize="small" />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {formatMacAddress(device.earbuds_mac)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={device.environment}
                          color={getEnvironmentColor(device.environment)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={device.is_blocked ? 'Blocked' : 'Active'}
                          color={device.is_blocked ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {device.last_attestation_security_level ? (
                          <Chip
                            label={device.last_attestation_security_level}
                            color={getSecurityLevelColor(device.last_attestation_security_level)}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {device.attestation_success_rate !== undefined ? (
                          `${(device.attestation_success_rate * 100).toFixed(1)}%`
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{device.total_requests.toLocaleString()}</TableCell>
                      <TableCell>{formatDate(device.first_seen)}</TableCell>
                      <TableCell>{formatDate(device.last_seen)}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={device.is_blocked ? 'Unblock Device' : 'Block Device'}>
                            <IconButton 
                              size="small" 
                              onClick={() => openActionDialog(device)}
                              disabled={actionLoading === device.id}
                            >
                              {actionLoading === device.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Block color={device.is_blocked ? 'success' : 'error'} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Block/Unblock Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedDevice?.is_blocked ? 'Unblock Device' : 'Block Device'}
        </DialogTitle>
        <DialogContent>
          {selectedDevice && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to {selectedDevice.is_blocked ? 'unblock' : 'block'} this device?
              </Typography>
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">Device ID:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedDevice.id}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Phone MAC:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatMacAddress(selectedDevice.phone_mac)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Earbuds MAC:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatMacAddress(selectedDevice.earbuds_mac)}</Typography>
              </Box>
              <TextField
                fullWidth
                label="Reason (Optional)"
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                multiline
                rows={3}
                sx={{ mt: 3 }}
                placeholder={`Reason for ${selectedDevice.is_blocked ? 'unblocking' : 'blocking'} this device...`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => selectedDevice && handleDeviceAction(selectedDevice, selectedDevice.is_blocked ? 'unblock' : 'block')}
            variant="contained"
            color={selectedDevice?.is_blocked ? 'success' : 'error'}
            disabled={actionLoading !== null}
          >
            {actionLoading ? <CircularProgress size={20} /> : (selectedDevice?.is_blocked ? 'Unblock' : 'Block')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Devices; 